import { useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import bcdLogo from "images/BCD-Logo-White-80x22.png";

import OrderEntryModel from "models/OrderEntryModel";

interface Props {
    model: OrderEntryModel
}


const TrackingNumbers = ({ model }: Props) => {

    const upsTracking = "https://www.ups.com/track?loc=en_US&tracknum=";
    const fedexTracking = "https://www.fedex.com/fedextrack/?trknbr=";
    const dbSchenker = "https://eschenker.dbschenker.com/app/tracking-public/?refNumber="; // 84090021079088 / ORD82247887
    const oldDominion = "https://www.odfl.com/us/en/tools/trace-track-ltl-freight/trace.html?proNumbers="; // 77844962975  -- PO: P26069
    const saia = "https://www.saia.com/track/details;pro="; // 10656661380    -- PO: 05722011/M-00001
    const xpo = "https://app.ltl.xpo.com/appjs/tracking/details/"; // 395598000      -- PO: 39240
    const dhl = "https://mydhl.express.dhl/us/en/tracking.html#/results?id="; // 5835900335      -- PO: 20220037
    const bestWay = "https://easytrackings.com/wp-json/easytrackings/v1/track?t=";
    const estes = 'https://www.estes-express.com/myestes/tracking/shipments';

    const [showEstes, setShowEstes] = useState<boolean>(false);

    const toggle = () => setShowEstes(!showEstes);

    const onClick = (trackingNumber: string) => {
        setShowEstes(true);
        navigator.clipboard.writeText(trackingNumber);
    };

    const openEstes = () => {
        window.open(estes, "_blank");
        setShowEstes(false);
    };

    const renderTrackingNumbers = () => {
        if (model.trackingNumbers) {
            if (model.shipper) {

                let numbers: string[] = [];

                if (model.trackingNumbers.includes(","))
                    numbers = model.trackingNumbers.split(",");
                else
                    numbers.push(model.trackingNumbers);

                let shipperLink = "";
                if (model.shipper.startsWith("UPS")) {
                    shipperLink = upsTracking;
                } else if (model.shipper.startsWith("FedEx")) {
                    shipperLink = fedexTracking;
                } else {
                    switch (model.shipper) {
                        case "Trinity Estes":
                            shipperLink = estes;
                            break;
                        case "Trinity Old Dominion":
                            shipperLink = oldDominion;
                            break;
                        case "Trinity Saia":
                            shipperLink = saia;
                            break;
                        case "Trinity XPO":
                            shipperLink = xpo;
                            break;
                        case "DHL":
                            shipperLink = dhl;
                            break;
                        case "DB Schenker":
                            shipperLink = dbSchenker;
                            break;
                        case "Best Way":
                            shipperLink = bestWay;
                            break;
                        default:
                            break;
                    }
                }

                if (shipperLink &&
                    shipperLink !== estes &&
                    shipperLink !== bestWay)
                    return numbers.map((n, index) => {
                        return (
                            <Col key={index}>
                                <a href={shipperLink + n} target="_blank" rel="noreferrer" aria-label={`Track shipment ${n}`} className="text-nowrap">
                                    {n}
                                    <FontAwesomeIcon icon='external-link' className="ms-1" />
                                </a>
                            </Col>
                        );
                    });

                if (shipperLink === estes)
                    return numbers.map((n, index) => {
                        return (
                            <Col key={index}>
                                <Button onClick={() => onClick(n)} color="link" aria-label={`Copy tracking number ${n} and open Estes`} className="ps-0 text-decoration-none text-nowrap">
                                    {n}
                                    <FontAwesomeIcon icon='window-maximize' className="ms-1" />
                                </Button>
                            </Col>
                        );
                    });
                return numbers.map((n, index) => {
                    return (
                        <Col key={index}>
                            {n}
                        </Col>
                    );
                });
            }
        }
        return model.trackingNumbers;
    }

    const renderEstesModal = () => {
        return (<Modal isOpen={showEstes} toggle={toggle} backdrop="static" size="sm">
            <ModalHeader toggle={toggle} className="bg-primary">
                <img src={bcdLogo} alt="BCD Logo White" />
            </ModalHeader>
            <ModalBody className="text-center">
                <b>Tracking number copied to clipboard. Click OK to continue.</b>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" size="sm" onClick={() => openEstes()}>
                    OK
                </Button>
                <Button color="secondary" onClick={toggle} size="sm">
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>);
    }

    return (<>
        <div className="d-flex flex-column flex-md-row justify-content-start me-5 pe-3">
            <div className="fs-5 fw-bold mb-3 text-nowrap ms-sm-5 w-25">
                {model.shipper ?
                    <>
                        {model.shipper || <span className="text-info">Check back soon</span>}
                        {model.shipper?.startsWith("UPS") &&
                            <FontAwesomeIcon icon={["fab", "ups"]} className="ms-2 bcd-ups-color" size="lg" />
                        }
                        {model.shipper?.startsWith("FedEx") &&
                            <FontAwesomeIcon icon={["fab", "fedex"]} className="ms-2 bcd-fedex-color" size="lg" />
                        }
                    </>
                    :
                    "Tracking Numbers"
                }
            </div>
            {model.trackingNumbers &&
                model.trackingNumbers.length > 0 ?
                <Row className="ps-sm-5">
                    {renderTrackingNumbers()}
                </Row> :
                <div className="mb-3">
                    <span className="fs-5">
                        None specified
                    </span>
                </div>
            }
        </div>
        {renderEstesModal()}
    </>);
};

export default TrackingNumbers;